import { galaxyOf, nodesOf } from "../core/world";
import {
  BALANCE as B,
  FACTIONS,
  HOME,
  NODES,
  ROLES,
  SHIPS,
  TECHS,
  COMMANDERS,
  PREFERRED_ROLE,
  PREFERENCES,
  asset,
  rank,
  maxMoves,
} from "../core/data";
import {
  act,
  actor,
  createGame,
  actionError,
  battleRound,
  chooseStance,
  concludeBattle,
  retreat,
  canRetreat,
  playerBattleSide,
  economicSummary,
  buildCost,
  shipCost,
  technologyCost,
  commissionCost,
  RuleError,
} from "../core/game";
import { shipMax, combatShip, aiStance, admiralBonuses } from "../core/combat";
import { observe, fleetContacts } from "../core/fog";
import {
  flagship,
  formation,
  shotsFor,
  battleBackground,
  battleSpriteHeight,
  planetHitRadius,
} from "./presentation";
import { buildingName, buildingAsset } from "./catalog";
import type { Shot } from "./presentation";
import { decide } from "../ai/strategist";
import {
  SaveStore,
  loadPreferences,
  savePreferences,
  exportSave,
  importFile,
  SLOTS,
} from "../persistence/storage";
import type { Preferences, SaveRecord, Slot } from "../persistence/storage";
import { AudioManager } from "../audio/manager";
import { t, factionName, LORE, BONUSES, TEXT } from "../i18n";
import type {
  FactionId,
  Role,
  GameState,
  GameAction,
  Stance,
  CombatUnit,
  BattleReport,
  Technology,
  Ability,
  Language,
  Fleet,
  ShipClass,
  Building,
} from "../core/types";
export const escape = (v: unknown) =>
  String(v ?? "").replace(
    /[&<>"']/g,
    (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[
        c
      ]!,
  );
const icon = (id: string) =>
  `<img class="icon" src="${asset("ui/" + id + ".svg")}" alt="" aria-hidden="true">`;
const img = (path: string, alt: string, cls = "") =>
  `<img class="${cls}" src="${asset(path)}" alt="${escape(alt)}" draggable="false">`;
const roman = (v: number) => ["—", "I", "II", "III", "IV", "V"][v] ?? String(v);
const planet = (id: string, nodes = NODES) => {
  const n = nodes[id];
  return n.type === "homeworld"
    ? `home-${n.startOwner}`
    : ((
        { temperate: "biosphere", nebula: "gas", anomaly: "gas" } as Record<
          string,
          string
        >
      )[n.visualVariant] ?? n.visualVariant);
};
type Modal =
  | "settings"
  | "research"
  | "fleets"
  | "anomalies"
  | "records"
  | "saves"
  | "help"
  | "credits"
  | "report"
  | "summary"
  | "combat-log"
  | null;
type GALAXYEdge = [string, string];
const abilityIcon: Record<Ability, string> = {
  repairPulse: "repair",
  phaseBarrier: "phase",
  slipstreamCorridor: "jump",
  sabotageProbe: "sabotage",
  ionTempest: "ion",
  sensorBurst: "scan",
};
export class App {
  game: GameState | null = null;
  preferences: Preferences;
  audio: AudioManager;
  store = new SaveStore();
  modal: Modal = null;
  private setupFaction: FactionId = "alliance";
  private setupRole: Role = "navigator";
  private opponents = 5;
  private difficulty: GameState["difficulty"] = "normal";
  private seed = "";
  private guided = true;
  private screen: "menu" | "setup" | "game" = "menu";
  private selectedSystem = HOME.alliance;
  private selectedFleet = "";
  private records: SaveRecord[] = [];
  private latest: SaveRecord | null = null;
  private reportIndex = -1;
  private confirmText = "";
  private confirmCallback: (() => void) | null = null;
  private toast = "";
  private toastTimer = 0;
  private autoTimer = 0;
  private auto = false;
  private aiTimer = 0;
  private pumping = false;
  private destroyed = false;
  private mapZoom = 1;
  private mapX = 0;
  private mapY = 0;
  private lastEvents = 0;
  private beam = 0;
  private shots: Shot[] = [];
  private shotTimers: number[] = [];
  private previousHull = new Map<string, number>();
  private roundDuration = 0;
  private flight: {
    view: GameState;
    from: string;
    to: string;
    fleet: string;
    faction: FactionId;
    ship: ShipClass;
    duration: number;
  } | null = null;
  private flightTimer = 0;
  private layoutObserver: ResizeObserver | null = null;
  private resize = () => {
    this.updateMap();
    this.updateBattleLayout();
  };
  private get displayGame() {
    return this.flight?.view ?? this.game;
  }
  private automaticSound = () => {
    if (!this.destroyed && !this.preferences.muted && this.audio.needsGesture)
      void this.audio.unlock();
  };
  private commandFx = "";
  private fxTimer = 0;
  private focusBeforeModal: string | null = null;
  private pendingImport: GameState | null = null;
  constructor(
    public root: HTMLElement,
    preferences = loadPreferences(),
  ) {
    this.preferences = preferences;
    this.audio = new AudioManager(preferences);
    root.addEventListener("click", this.click);
    root.addEventListener("change", this.change);
    root.addEventListener("input", this.input);
    document.addEventListener("keydown", this.key);
    document.addEventListener("pointerdown", this.automaticSound, true);
    document.addEventListener("keydown", this.automaticSound, true);
    root.addEventListener("pointerdown", this.pointer);
    root.addEventListener("wheel", this.wheel, { passive: false });
    window.addEventListener("resize", this.resize);
    root.addEventListener("pointerover", (e) => {
      if (
        (e.target as Element).closest("button") &&
        !(e.target as Element)
          .closest("button")
          ?.contains((e as PointerEvent).relatedTarget as Node)
      )
        void this.audio.sfx("hover");
    });
    this.render();
    if (!preferences.muted) void this.audio.unlock();
    void this.store
      .init()
      .then(async () => {
        await this.refreshSaves();
        if (this.store.mode === "memory") this.notify("storage.memory");
      })
      .catch(() => {});
  }
  tr = (key: string) => t(this.preferences.language, key);
  private fn = (f: FactionId) => factionName(this.preferences.language, f);
  private bn = (f: FactionId, b: Building) =>
    buildingName(f, b, this.preferences.language);
  private n = (v: number) =>
    new Intl.NumberFormat(this.preferences.language).format(v);
  private get nodes() {
    return nodesOf(this.game);
  }
  private sn = (id: string) =>
    this.nodes[id]?.name[this.preferences.language] ?? id;
  private button(label: string, action: string, ic = "", extra = "", cls = "") {
    return `<button type="button" class="${cls}" data-action="${action}" ${extra}>${ic ? icon(ic) : ""}${escape(label)}</button>`;
  }
  private order(a: GameAction, label: string, ic = "", cost = "") {
    if (!this.game) return "";
    const error = this.flight
      ? "fleet.travel"
      : actor(this.game) !== this.game.player
        ? "enemy.turn"
        : actionError(this.game, a);
    return `<div class="order">${this.button(label, "order", ic, `data-order="${escape(JSON.stringify(a))}" ${error ? "disabled" : ""}`, cost ? "purchase" : "")}${cost ? `<span class="cost">${escape(cost)}</span>` : ""}${error ? `<small class="reason">${escape(this.tr(error))}</small>` : ""}</div>`;
  }
  private language() {
    return `<div class="languages" aria-label="${this.tr("language")}">${(["en", "de"] as Language[]).map((l) => this.button(l.toUpperCase(), "language", "", `data-language="${l}" aria-pressed="${this.preferences.language === l}"`)).join("")}</div>`;
  }
  render() {
    if (this.destroyed) return;
    const active = document.activeElement as HTMLElement | null,
      focus = active?.getAttribute("data-focus") ?? active?.id,
      scrolls = new Map(
        [...this.root.querySelectorAll<HTMLElement>("[data-scroll]")].map(
          (e) => [e.dataset.scroll, e.scrollTop],
        ),
      );
    document.documentElement.lang = this.preferences.language;
    document.body.classList.toggle(
      "reduced-motion",
      this.preferences.reducedMotion,
    );
    document.body.classList.toggle("high-contrast", this.preferences.contrast);
    const f = this.game?.player ?? this.setupFaction;
    this.root.style.setProperty("--faction", B.factions[f].palette[0]);
    this.root.innerHTML =
      (this.screen === "game" && this.game
        ? this.gameView()
        : this.menuView()) +
      this.modalView() +
      this.confirmView() +
      (this.commandFx
        ? `<div class="command-effect" aria-hidden="true"><i class="vfx ${this.commandFx}"></i></div>`
        : "") +
      (this.toast
        ? `<div class="toast" role="status">${escape(this.toast)}</div>`
        : "");
    this.root
      .querySelectorAll<HTMLElement>("[data-action]")
      .forEach((el, i) => (el.dataset.focus = `ui-${el.dataset.action}-${i}`));
    for (const el of this.root.querySelectorAll<HTMLElement>("[data-scroll]"))
      el.scrollTop = scrolls.get(el.dataset.scroll) ?? 0;
    if (focus) {
      const el = this.root.querySelector<HTMLElement>(
        `[data-focus="${CSS.escape(focus)}"],#${CSS.escape(focus)}`,
      );
      el?.focus({ preventScroll: true });
    }
    this.updateMap();
    this.layoutObserver?.disconnect();
    this.updateBattleLayout();
    const arena = this.root.querySelector(".space-arena");
    if (arena && typeof ResizeObserver !== "undefined") {
      this.layoutObserver ??= new ResizeObserver(() =>
        this.updateBattleLayout(),
      );
      this.layoutObserver.observe(arena);
    }
    const scene =
      this.screen !== "game"
        ? "menu"
        : this.flight
          ? "map"
          : this.game?.phase === "battle"
            ? "battle"
            : this.game?.phase === "map"
              ? "map"
              : "finale";
    this.audio.scene(scene, f);
  }
  private menuView() {
    const f = this.setupFaction,
      l = this.preferences.language;
    return `<main class="menu-screen" style="--hero:url('${asset(this.screen === "menu" ? "backgrounds/nebula-0.webp" : `factions/${f}/hero.webp`)}')"><header class="menu-header"><a class="brand" href="#" data-action="menu-home">${icon("logo")}<span>STELLAR <b>EMPIRE</b></span></a><div class="header-tools">${this.language()}${this.button(this.tr(!this.preferences.muted ? "sound.enabled" : "sound.enable"), "sound", this.preferences.muted ? "mute" : "sound")}${this.button(this.tr("settings"), "settings", "settings")}</div></header><div class="menu-body" data-scroll="menu">${this.screen === "menu" ? `<section class="title-scene"><p class="eyebrow">${this.tr("region")} · 1.2</p><h1>STELLAR<br><span>EMPIRE</span></h1><p class="tagline">${this.tr("tagline")}</p><div class="title-actions">${this.button(this.tr("new"), "new", "play", "", "primary")}${this.latest ? this.button(this.tr("continue"), "continue", "load") : ""}${this.button(this.tr("saves"), "saves", "save")}</div><div class="title-secondary">${this.button(this.tr("help"), "help", "book")}</div><p class="micro">${this.tr("capital.goal")}</p></section><aside class="title-faction"><p class="eyebrow">${this.fn(f)}</p><p>${escape(LORE[f][l === "de" ? 1 : 0].split(". ")[0] + ".")}</p><div class="faction-dots">${FACTIONS.map((x) => this.button(this.fn(x), "preview", "", `data-faction="${x}" aria-pressed="${x === f}" style="--dot:${B.factions[x].palette[0]}"`)).join("")}</div></aside>` : this.setupView()}</div><div class="menu-rule"><span>36 ${this.tr("systems")} · 6 ${this.tr("civilizations")} · ${this.tr("sim")}</span></div></main>`;
  }
  private setupView() {
    const f = this.setupFaction;
    return `<section class="setup"><div class="section-heading"><div><p class="eyebrow">01 / ${this.tr("new")}</p><h1>${this.tr("choose")}</h1></div>${this.button(this.tr("back"), "menu-home", "close")}</div><div class="faction-tabs" role="group" aria-label="${this.tr("choose")}">${FACTIONS.map((x) => `<button type="button" class="faction-tab" data-action="faction" data-faction="${x}" aria-pressed="${x === f}" style="--tab-color:${B.factions[x].palette[0]}">${img(`factions/${x}/hero.webp`, this.fn(x))}<span>${this.fn(x)}</span></button>`).join("")}</div><div class="setup-grid"><article class="civilization"><p class="eyebrow">${this.tr("home")} / ${B.factions[f].home}</p><h2>${this.fn(f)}</h2><p class="lore">${escape(LORE[f][this.preferences.language === "de" ? 1 : 0])}</p><div class="trait">${icon("anomaly")}<div><b>${this.tr("bonus")}</b><p>${escape(BONUSES[f][this.preferences.language === "de" ? 1 : 0])}</p></div></div><div class="ship-parade">${SHIPS.map((s) => `<div>${img(`ships/${f}/${s}.webp`, B.factions[f].ships[s])}<strong>${B.factions[f].ships[s]}</strong><small>${this.tr(s)}</small></div>`).join("")}</div></article><section class="setup-options"><h3>${this.tr("doctrine")}</h3><div class="commander-options">${ROLES.map((r) => `<button type="button" class="commander-option" data-action="role" data-role="${r}" aria-pressed="${this.setupRole === r}">${img(`factions/${f}/${r}.webp`, COMMANDERS[f][r])}<span><b>${COMMANDERS[f][r]}</b><small>${this.tr(r)}${PREFERRED_ROLE[f] === r ? " · ★" : ""}</small></span></button>`).join("")}</div><p class="muted">${this.tr(this.setupRole + ".desc")}</p><small>${this.tr("doctrine.hint")}</small><h3>${this.tr("setup")}</h3><div class="form-grid"><label>${this.tr("opponents")}<select id="opponents">${[1, 2, 3, 4, 5].map((v) => `<option ${v === this.opponents ? "selected" : ""} value="${v}">${v}</option>`).join("")}</select></label><label>${this.tr("difficulty")}<select id="difficulty">${["easy", "normal", "hard"].map((v) => `<option ${v === this.difficulty ? "selected" : ""} value="${v}">${this.tr(v)}</option>`).join("")}</select></label></div><label>${this.tr("seed")}<input id="seed" maxlength="60" value="${escape(this.seed)}" placeholder="Meridian-01" autocomplete="off"></label><small>${this.tr("seed.hint")}</small><label class="check"><input id="guided" type="checkbox" ${this.guided ? "checked" : ""}>${this.tr("tutorial")}</label><p>${this.tr("launch.hint")}</p>${this.button(this.tr("start"), "start", "play", "", "primary wide")}</section></div></section>`;
  }
  private gameView() {
    const g = this.displayGame!,
      fs = g.factions[g.player],
      e = economicSummary(g, g.player),
      own = g.fleets.filter((x) => x.faction === g.player),
      busy = !!this.flight || (actor(g) !== g.player && g.phase === "map");
    return `<main class="game-screen ${g.phase === "battle" ? "in-battle" : ""}"><header class="game-header"><button class="brand compact" data-action="menu" type="button">${icon("logo")}<span>STELLAR <b>EMPIRE</b></span></button><div class="campaign-id"><strong>${this.fn(g.player)}</strong><span>${this.tr("turn")} ${g.turn}</span></div><div class="resources"><div title="${this.tr("net")}">${icon("credits")}<span><b>${this.n(fs.credits)} C</b><small class="${e.net < 0 ? "danger-text" : ""}">${e.net >= 0 ? "+" : ""}${this.n(e.net)} / ${this.tr("turn")}</small></span></div><div>${icon("research")}<span><b>${this.n(fs.rp)} RP</b><small>+${e.rp} / ${this.tr("turn")}</small></span></div><div>${icon("anomaly")}<span><b>${fs.charge} AC</b><small>${this.tr(fs.specialUsed ? "used" : "ready")}</small></span></div><div class="fleet-count">${icon("fleet")}<b>${own.length}/3</b></div></div><div class="header-tools">${this.language()}${this.button(this.tr("saves"), "saves", "save", "", "icon-button")}${this.button(this.tr("settings"), "settings", "settings", "", "icon-button")}</div></header>${
      g.phase === "victory" || g.phase === "defeat"
        ? this.endingView()
        : g.phase === "battle"
          ? this.battleView()
          : `<nav class="command-nav" aria-label="${this.tr("menu")}">${[
              ["fleets", "fleet"],
              ["research", "research"],
              ["anomalies", "anomaly"],
              ["records", "book"],
              ["help", "help"],
            ]
              .map(([key, ic]) => this.button(this.tr(key), key, ic))
              .join(
                "",
              )}<span class="objective">${this.tr("capital.goal")}</span>${this.button(this.tr("end"), "end", "turn", busy ? "disabled" : "", "primary")}</nav><div class="strategic-layout"><section class="chart-section"><div class="chart-heading"><div><p class="eyebrow">${this.tr("map")}</p><h2>${this.tr("region")}</h2></div><div class="chart-tools">${this.button(this.tr("zoom.out"), "zoom-out", "minus", "", "icon-button")}${this.button(this.tr("zoom.in"), "zoom-in", "plus", "", "icon-button")}${this.button(this.tr("center"), "center", "home", "", "icon-button")}</div></div><div class="chart-viewport" id="chart" aria-label="${this.tr("map")}">${this.chartView()}${this.flight ? `<div class="flight-status" role="status">${icon("jump")}${this.tr("fleet.travel")} · ${escape(this.sn(this.flight.to))}</div>` : ""}</div><div class="chart-caption"><span>${this.tr("zoom.hint")}</span><span>◆ ${this.fn(g.player)} · ◇ ${this.tr("neutral.owner")} · ○ ${this.tr("uncharted")}</span></div><div class="fleet-dock">${own.map((fl) => `<button type="button" class="fleet-tile" data-action="fleet-select" data-id="${fl.id}" aria-pressed="${this.selectedFleet === fl.id}">${img(`factions/${g.player}/${fl.admiral.role}.webp`, COMMANDERS[g.player][fl.admiral.role])}<span><b>${COMMANDERS[g.player][fl.admiral.role]}</b><small>${this.sn(fl.system)} · ${fl.ships.length}/7</small><small>${this.tr("moves")} ${fl.moves}/${maxMoves(fl.admiral.role, fl.admiral.xp)} · ${this.tr("rank")} ${roman(rank(fl.admiral.xp))}</small></span></button>`).join("") || `<p>${this.tr("no.fleets")}</p>`}</div></section><aside class="system-panel" data-scroll="system">${this.systemView()}</aside></div>${fs.shortage ? `<div class="supply-warning" title="${this.tr("supply.hint")}">${icon("warning")}${this.tr("supply")} · −${fs.shortage * 10}% ${this.tr("damage")}</div>` : ""}${g.tutorial >= 0 ? `<aside class="tutorial"><p>${this.tr("tutorial." + Math.min(g.tutorial, 4))}</p>${this.button(this.tr("tutorial.next"), "tutorial-next", "play")}${this.button(this.tr("tutorial.dismiss"), "tutorial-dismiss", "close", "", "icon-button")}</aside>` : ""}${busy && !this.flight ? `<div class="enemy-progress" role="status"><span class="spinner"></span>${this.flight ? this.tr("fleet.travel") : this.tr("enemy.turn") + " · " + this.fn(actor(g))}</div>` : ""}`
    }</main>`;
  }
  private chartView() {
    const g = this.displayGame!,
      nodes = nodesOf(g),
      o = observe(g, g.player),
      known = Object.fromEntries(o.systems.map((s) => [s.id, s])),
      selected = g.fleets.find((f) => f.id === this.selectedFleet),
      edges: GALAXYEdge[] = [];
    for (const n of galaxyOf(g).nodes)
      for (const id of n.neighbors)
        if (n.id < id && known[n.id] && known[id]) edges.push([n.id, id]);
    const contacts = fleetContacts(g, g.player).filter(
      (c) => c.id !== this.flight?.fleet,
    );
    return `<svg class="chart" viewBox="-35 -20 1070 800" role="group" aria-label="${this.tr("map")}"><defs><radialGradient id="sensor-glow"><stop stop-color="#74d6cf" stop-opacity=".12"/><stop offset="1" stop-color="#74d6cf" stop-opacity="0"/></radialGradient></defs><g id="map-layer"><text class="sector-label" x="500" y="375" text-anchor="middle">MERIDIAN</text>${o.systems
      .filter((s) => s.visible)
      .map(
        (s) =>
          `<ellipse cx="${nodes[s.id].x}" cy="${nodes[s.id].y}" rx="86" ry="72" fill="url(#sensor-glow)" pointer-events="none"/>`,
      )
      .join(
        "",
      )}${edges.map(([a, b]) => `<line class="route known" x1="${nodes[a].x}" y1="${nodes[a].y}" x2="${nodes[b].x}" y2="${nodes[b].y}"/>`).join("")}${o.systems
      .map((s) => {
        const n = nodes[s.id],
          owner = s.owner,
          c =
            owner && owner !== "unknown"
              ? B.factions[owner].palette[0]
              : "#99a8b7",
          reachable =
            !!selected &&
            nodes[selected.system].neighbors.includes(n.id) &&
            selected.moves > 0;
        return `<g role="button" tabindex="0" data-system="${n.id}" data-action="system" aria-label="${escape(this.sn(n.id))}" class="map-node known ${s.visible ? "visible" : ""} ${this.selectedSystem === n.id ? "selected" : ""} ${reachable ? "reachable" : ""}" style="--node:${c}"><circle class="hit-area" cx="${n.x}" cy="${n.y}" r="30" fill="transparent" pointer-events="all"/><circle class="orbit" cx="${n.x}" cy="${n.y}" r="22" pointer-events="none"/><image href="${asset(`planets/${planet(n.id, nodes)}.webp`)}" x="${n.x - 18}" y="${n.y - 18}" width="36" height="36" pointer-events="none"/><text x="${n.x}" y="${n.y + 38}" text-anchor="middle">${escape(this.sn(n.id))}</text><text class="star-label" x="${n.x}" y="${n.y - 29}" text-anchor="middle">${"★".repeat(n.stars)}${n.type === "homeworld" ? " ◆" : ""}</text></g>`;
      })
      .join("")}${contacts
      .map((c) => {
        const n = nodes[c.system],
          siblings = contacts.filter((x) => x.system === c.system),
          index = siblings.indexOf(c),
          x = n.x + 12,
          y = n.y - 5 + index * 18;
        return `<g class="map-fleet ${c.own ? "own" : "enemy"} ${this.selectedFleet === c.id ? "selected" : ""}" data-fleet="${c.id}" ${c.own ? `role="button" tabindex="0" data-action="fleet-select" data-id="${c.id}"` : `role="button" tabindex="0" data-action="system" data-system="${c.system}"`} aria-label="${escape(this.fn(c.faction) + ": " + this.tr(c.flagship) + " · " + c.size)}" style="--fleet-color:${B.factions[c.faction].palette[0]}"><title>${escape(this.fn(c.faction))} · ${escape(B.factions[c.faction].ships[c.flagship])} · ${c.size}/7</title><rect class="fleet-hit-area" x="${x - 7}" y="${y - 10}" width="60" height="38" fill="transparent" pointer-events="all"/><ellipse cx="${x + 20}" cy="${y + 22}" rx="24" ry="5"/><image href="${asset(`ships/${c.faction}/${c.flagship}.webp`)}" x="${x - 7}" y="${y - 10}" width="60" height="38" pointer-events="none"/><text class="contact-count" x="${x + 48}" y="${y + 21}">${c.size}</text></g>`;
      })
      .join("")}${this.flight ? this.flightView() : ""}</g></svg>`;
  }
  private flightView() {
    const f = this.flight!,
      a = this.nodes[f.from],
      b = this.nodes[f.to],
      angle = (Math.atan2(b.y - a.y, b.x - a.x) * 180) / Math.PI;
    return `<g class="flight-route" aria-hidden="true"><line x1="${a.x}" y1="${a.y}" x2="${b.x}" y2="${b.y}"/><g transform="translate(${a.x} ${a.y})"><g class="flight-traveller" style="--dx:${b.x - a.x}px;--dy:${b.y - a.y}px;--flight-time:${f.duration}ms"><g transform="rotate(${angle})"><path class="engine-trail" d="M -58 0 L -16 -5 L -16 5 Z"/><image href="${asset(`ships/${f.faction}/${f.ship}.webp`)}" x="-28" y="-18" width="60" height="36"/></g></g></g></g>`;
  }
  private startFlight(view: GameState, a: GameAction) {
    if (
      (a.type !== "move" &&
        !(a.type === "special" && a.ability === "slipstreamCorridor")) ||
      this.preferences.reducedMotion
    )
      return 0;
    const fleet = view.fleets.find((f) => f.id === a.fleet);
    if (
      !fleet ||
      !a.to ||
      !fleetContacts(view, view.player).some((f) => f.id === fleet.id) ||
      !observe(view, view.player).systems.some((s) => s.id === a.to)
    )
      return 0;
    const duration = Math.round(800 / this.preferences.speed);
    this.flight = {
      view,
      from: fleet.system,
      to: a.to,
      fleet: fleet.id,
      faction: fleet.faction,
      ship: flagship(fleet.ships),
      duration,
    };
    window.clearTimeout(this.flightTimer);
    this.flightTimer = window.setTimeout(() => {
      this.flight = null;
      this.render();
    }, duration);
    return duration;
  }
  private clearFlight() {
    window.clearTimeout(this.flightTimer);
    this.flight = null;
  }
  private systemView() {
    const g = this.displayGame!,
      s = observe(g, g.player).systems.find(
        (s) => s.id === this.selectedSystem,
      ),
      fl = g.fleets.find((f) => f.id === this.selectedFleet);
    if (!s)
      return `<div class="unknown-system">${icon("scan")}<h2>${this.tr("uncharted")}</h2><p>${this.tr("enemy.info")}</p></div>`;
    const n = this.nodes[s.id],
      own = s.owner === g.player;
    return `<div class="planet-portrait">${img(`planets/${planet(s.id, this.nodes)}.webp`, this.sn(s.id))}<span class="eyebrow">${n.type === "homeworld" ? this.tr("home") : this.tr("fog")}</span></div><div class="system-heading"><span class="stars">${"★".repeat(n.stars)}</span><h2>${escape(this.sn(s.id))}</h2><p>${this.tr("owner")} · <b>${s.owner === "unknown" ? this.tr("unknown") : s.owner ? this.fn(s.owner) : this.tr("neutral.owner")}</b></p></div><p class="contact-status">${icon(s.visible ? "scan" : "info")}${this.tr(s.visible ? "fog" : s.seen >= 0 ? "stale" : "charted")}${!s.visible && s.seen >= 0 ? ` · ${this.tr("seen")} ${s.seen}` : ""}</p>${s.barrier ? `<div class="badge">${icon("phase")}${this.tr("phaseBarrier")}</div>` : ""}${s.ships ? `<div class="contact-ships">${SHIPS.map((type) => `<span>${icon(type)}${this.tr(type)} <b>${s.ships!.filter((x) => x === type).length}</b></span>`).join("")}${s.bastion ? `<span>${icon("bastion")}${s.owner && s.owner !== "unknown" ? this.bn(s.owner, "bastion") : this.tr("bastion")} ${roman(s.bastion)}</span>` : ""}</div>` : `<p class="muted">${this.tr("enemy.info")}</p>`}${fl && s.id !== fl.system ? this.order({ type: "move", fleet: fl.id, to: s.id }, this.tr(own ? "move" : "attack"), "jump") : ""}${
      own
        ? `<div class="system-economy"><span>${this.tr("income")}<b>${B.starCreditsPerTurn[String(n.stars) as keyof typeof B.starCreditsPerTurn]} C</b></span><span>${this.tr("economic")}<b>${s.mine ? this.bn(g.player, "mine") + " " + roman(s.mine) : s.research ? this.bn(g.player, "research") + " " + roman(s.research) : this.tr("empty")}</b></span><span>${this.tr("military")}<b>${s.bastion ? this.bn(g.player, "bastion") + " " + roman(s.bastion) : this.tr("empty")}${s.grid ? " + " + this.bn(g.player, "grid") + " " + roman(s.grid) : ""}</b></span></div><h3>${this.tr("build")}</h3><div class="building-grid">${(
            ["mine", "research", "bastion", "grid"] as const
          )
            .map((b) => {
              const level = s[b] ?? 0,
                limit = b === "grid" ? 2 : 3;
              return `<article title="${escape(this.tr(b + ".desc"))}">${img(buildingAsset(g.player, b), this.bn(g.player, b))}<strong>${escape(this.bn(g.player, b))} ${roman(level)}</strong><small class="building-function">${this.tr(b)}</small>${this.order({ type: "build", system: s.id, building: b }, level >= limit ? this.tr("max") : this.tr("level") + " " + roman(level + 1), b, level >= limit ? "" : `${buildCost(g, g.player, b, level + 1)} C`)}</article>`;
            })
            .join(
              "",
            )}</div><small>${this.tr("construction.limit")}</small>${s.bastion ? `<h3>${this.tr("shipyard")}</h3>${fl?.system === s.id ? this.shipOffers(fl) : `<p>${this.tr("select.fleet")}</p>`}${this.button(this.tr("commission"), "fleets", "admiral")}` : ""}`
        : ""
    }`;
  }
  private shipOffers(fl: Fleet) {
    const g = this.game!,
      fs = g.factions[g.player];
    return `<div class="ship-offers">${SHIPS.map((type) => {
      const max = shipMax(type, g.player, fs.tech.shields);
      const stats = combatShip(
        { id: "purchase-preview", type, hull: max.hull, shields: max.shields },
        g.player,
        fs,
        fl.admiral,
      );
      const fields = [
        ["hull", "hull", stats.maxHull],
        ["shields", "shield", stats.maxShields],
        ["damage", "attack", stats.attack],
        [
          "accuracy",
          "accuracy",
          Math.round(
            Math.min(0.98, stats.accuracy + stats.accuracyBonus) * 100,
          ) + "%",
        ],
        ["initiative", "initiative", stats.initiative],
        [
          "evasion",
          "evasion",
          this.n(Math.round(stats.evasion * 1000) / 10) + "%",
        ],
      ];
      return `<article class="ship-offer">${img(`ships/${g.player}/${type}.webp`, B.factions[g.player].ships[type])}<h4>${B.factions[g.player].ships[type]}</h4><small>${this.tr(type)} · ${this.tr("upkeep")} ${B.ships[type].upkeep} C</small><dl>${fields.map(([label, ic, value]) => `<div${label === "accuracy" ? ` title="${escape(this.tr("accuracy.hint"))}"` : ""}><dt>${icon(String(ic))}${this.tr(String(label))}</dt><dd>${value}</dd></div>`).join("")}</dl>${this.order({ type: "ship", fleet: fl.id, ship: type }, this.tr("build"), type, `${shipCost(g, g.player, type)} C`)}</article>`;
    }).join("")}</div>`;
  }
  private battleOutcome(
    outcome: BattleReport["outcome"],
    attacker: FactionId | null,
  ) {
    if (outcome === "active") return "battle.control";
    const playerAttacks = attacker === this.game?.player;
    if (outcome === "retreat")
      return playerAttacks ? "battle.retreat" : "battle.enemy.retreat";
    return (outcome === "attacker") === playerAttacks
      ? "battle.won"
      : "battle.lost";
  }
  private battleView() {
    const g = this.game!,
      b = g.battle!,
      side = playerBattleSide(g),
      active = b.status === "active";
    return `<section class="battle-screen" style="--battle-bg:url('${asset(battleBackground(g))}')"><div class="battle-heading"><div><p class="eyebrow">${this.tr("sim")} · ${this.tr("round")} ${b.round} / 24</p><h1>${escape(this.sn(b.system))}</h1></div><div class="battle-seed">${this.tr("seed")} ${b.seed}<small>${this.tr("battle.limit")}</small></div></div><div class="battle-arena space-arena">${(side ===
    "attacker"
      ? (["attacker", "defender"] as const)
      : (["defender", "attacker"] as const)
    )
      .map((which) => {
        const s = b[which];
        return `<section class="battle-side ${which} ${side === which ? "player-side" : "enemy-side"}"><header><div><span class="eyebrow">${this.tr(which)}${side === which ? " ◆" : ""}</span><h2>${s.faction ? this.fn(s.faction) : this.tr("neutral.owner")}</h2></div><span class="badge">${this.tr(s.stance)}</span></header>${s.admiral ? `<div class="battle-admiral">${img(`factions/${s.style}/${s.admiral.role}.webp`, COMMANDERS[s.style][s.admiral.role])}<span><b>${COMMANDERS[s.style][s.admiral.role]}</b><small>${this.tr("rank")} ${roman(rank(s.admiral.xp))} · ${this.tr(s.admiral.role)}</small></span></div>` : ""}<div class="combat-units">${s.units.map((u, i) => this.unitView(u, i, s.units.length, side !== which)).join("")}</div></section>`;
      })
      .join(
        "",
      )}${this.weaponView()}</div><div class="battle-console"><div class="stance-controls" role="group" aria-label="${this.tr("doctrine")}">${(["offensive", "neutral", "defensive"] as Stance[]).map((s, i) => this.button(`${i + 1} · ${this.tr(s)}`, "stance", s === "offensive" ? "attack" : s === "defensive" ? "defense" : "neutral", `data-stance="${s}" aria-pressed="${b[side].stance === s}" title="${escape(this.tr(s + ".desc"))}" ${active ? "" : "disabled"}`)).join("")}</div><p class="stance-description">${this.tr(b[side].stance + ".desc")}</p><div class="battle-actions">${active ? `${this.button(this.tr("next"), "next", "play", this.auto ? "disabled" : "", "primary")}${this.button(this.tr(this.auto ? "pause" : "auto"), "auto", this.auto ? "pause" : "play")}${this.button(this.tr("skip"), "skip", "skip")}${this.button(this.tr("retreat"), "retreat", "retreat", canRetreat(g) && b.round >= 1 ? "" : "disabled", canRetreat(g) ? "" : "unavailable")}` : this.button(this.tr("report"), "resolve", "book", "", "primary")}<span class="muted">${this.tr(this.battleOutcome(b.status, b.attacker.faction))}</span></div><div class="battle-status"><small>${this.tr("retreat.hint")}</small>${this.button(this.tr("log") + " · " + b.events.length, "combat-log", "book")}</div></div></section>`;
  }
  private combatLogView() {
    const b = this.game?.battle;
    if (!b) return "";
    return `<div class="combat-log"><div data-scroll="combatlog">${b.events
      .slice(-150)
      .map(
        (e) =>
          `<p class="event-${e.type}"><span>${e.round}</span> ${escape(e.type === "end" ? this.tr("end.battle") : e.type === "retreat" ? this.tr(this.battleOutcome("retreat", b.attacker.faction)) : `${this.unitName([...b.attacker.units, ...b.defender.units].find((u) => u.id === e.source))} ${this.tr(e.type)} ${this.unitName([...b.attacker.units, ...b.defender.units].find((u) => u.id === e.target))}${e.damage ? " −" + e.damage : ""}`)}</p>`,
      )
      .join("")}</div></div>`;
  }
  private unitName(u?: CombatUnit) {
    return u
      ? u.type === "bastion"
        ? this.bn(u.faction, "bastion")
        : B.factions[u.faction].ships[u.type]
      : "";
  }
  private weaponView() {
    return `<div class="weapons-plane" aria-hidden="true"><svg class="weapon-layer" viewBox="0 0 1000 600" preserveAspectRatio="none">${this.shots
      .map((s, i) => {
        const color = B.factions[s.source.faction].palette[0],
          d = `M ${s.from.x} ${s.from.y} L ${s.to.x} ${s.to.y}`;
        return `<g class="weapon-shot ${s.source.type === "dreadnought" ? "plasma-shot" : "beam-shot"} ${s.event.type === "miss" ? "miss-shot" : ""}" data-source="${s.event.source}" data-target="${s.event.target}" style="--shot-color:${color};--shot-delay:${s.delay}ms;--impact-delay:${s.impact}ms;--shot-duration:${Math.round(380 / this.preferences.speed)}ms"><path class="beam-glow" d="${d}"/><path class="beam-core" d="${d}"/><circle class="muzzle-flash" cx="${s.from.x}" cy="${s.from.y}" r="12"/>${s.event.type === "hit" ? `<circle class="shot-impact ${s.destroys ? "fatal-impact" : ""}" cx="${s.to.x}" cy="${s.to.y}" r="${s.destroys ? 37 : 22}"/><text class="shot-damage" x="${s.to.x}" y="${s.to.y - 28}">−${s.event.damage}</text>` : ""}</g>`;
      })
      .join("")}</svg></div>`;
  }
  private unitView(
    u: CombatUnit,
    index: number,
    count: number,
    enemy: boolean,
  ) {
    const b = this.game!.battle!,
      p = formation(count, index, enemy),
      death = this.shots.find((s) => s.event.target === u.id && s.destroys),
      dying = !u.hull && !!this.previousHull.get(u.id) && death;
    return `<article class="combat-unit space-unit ${u.hull || dying ? "" : "destroyed"} ${dying ? "dying" : ""} ship-${u.type}" data-unit="${u.id}" style="--unit-x:${p.x / 10}%;--unit-y:${p.y / 6}%;--death-delay:${death?.impact ?? 0}ms" aria-label="${escape(this.unitName(u))}: ${this.tr("hull")} ${u.hull}/${u.maxHull}, ${this.tr("shields")} ${u.shields}/${u.maxShields}"><div class="combat-ship">${img(u.type === "bastion" ? buildingAsset(u.faction, "bastion") : `ships/${u.faction}/${u.type}.webp`, this.unitName(u))}</div><div class="unit-info"><b>${escape(this.unitName(u))}</b><div class="stat-bar shield" title="${this.tr("shields")}"><i style="width:${u.maxShields ? (u.shields / u.maxShields) * 100 : 0}%"></i><span>${u.shields}/${u.maxShields}</span></div><div class="stat-bar hull" title="${this.tr("hull")}"><i style="width:${(u.hull / u.maxHull) * 100}%"></i><span>${u.hull}/${u.maxHull}</span></div></div></article>`;
  }
  private clearShots() {
    this.shotTimers.forEach((id) => window.clearTimeout(id));
    this.shotTimers = [];
    this.shots = [];
    this.previousHull.clear();
    this.roundDuration = 0;
  }
  private endingView() {
    const g = this.game!,
      win = g.phase === "victory",
      owned = Object.values(g.systems).filter(
        (s) => s.owner === g.player,
      ).length;
    return `<section class="ending" style="--hero:url('${asset(`factions/${g.player}/hero.webp`)}')"><div><p class="eyebrow">${this.tr("statistics")} · ${this.fn(g.player)}</p>${icon(win ? "victory" : "defeat")}<h1>${this.tr(win ? "victory" : "defeat")}</h1><p>${this.tr(win ? "victory.desc" : "defeat.desc")}</p><div class="ending-stats"><span><b>${g.turn}</b>${this.tr("turn")}</span><span><b>${owned}/36</b>${this.tr("systems")}</span><span><b>${g.reports.length}</b>${this.tr("battle")}</span></div><div class="button-row">${this.button(this.tr("restart"), "new", "play", "", "primary")}${this.button(this.tr("reports"), "records", "book")}${this.button(this.tr("export"), "export", "export")}${this.button(this.tr("menu"), "menu-home", "home")}</div></div></section>`;
  }
  private modalView() {
    if (!this.modal) return "";
    const m = this.modal,
      title =
        m === "combat-log"
          ? "log"
          : m === "report"
            ? "report"
            : m === "summary"
              ? "summary"
              : m,
      content =
        m === "combat-log"
          ? this.combatLogView()
          : m === "settings"
            ? this.settingsView()
            : m === "research"
              ? this.researchView()
              : m === "fleets"
                ? this.fleetsView()
                : m === "anomalies"
                  ? this.anomalyView()
                  : m === "saves"
                    ? this.savesView()
                    : m === "records" || m === "summary"
                      ? this.recordsView(m === "summary")
                      : m === "report"
                        ? this.reportView(this.game?.reports[this.reportIndex])
                        : m === "credits"
                          ? `<p>${this.tr("credits.text")}</p><p>${this.tr("help.original")}</p><p>© by www.perplex.click</p>`
                          : this.helpView();
    return `<div class="modal-overlay"><section class="modal ${m === "combat-log" ? "battle-log" : m}" role="dialog" aria-modal="true" aria-labelledby="modal-title" tabindex="-1"><header><div><p class="eyebrow">STELLAR EMPIRE</p><h2 id="modal-title">${this.tr(title)}</h2></div>${this.button(this.tr("close"), "close", "close", "", "icon-button")}</header><div class="modal-body" data-scroll="modal">${content}</div></section></div>`;
  }
  private settingsView() {
    const p = this.preferences;
    return `<div class="settings-grid"><section><h3>${this.tr("language")}</h3>${this.language()}<h3>${this.tr("sound.enable")}</h3>${this.button(this.tr(!this.preferences.muted ? "sound.enabled" : "sound.enable"), "sound", "sound")}${this.audio.blocked ? `<p class="warning">${this.tr("sound.blocked")}</p>` : ""}${(["master", "music", "sfx"] as const).map((k) => `<label class="range-label" for="${k}">${this.tr(k)} <b>${Math.round(p[k] * 100)}%</b><input type="range" min="0" max="100" id="${k}" value="${Math.round(p[k] * 100)}"></label>`).join("")}<label class="check"><input id="muted" type="checkbox" ${p.muted ? "checked" : ""}>${this.tr("mute")}</label></section><section><h3>${this.tr("settings")}</h3><label class="check"><input id="reducedMotion" type="checkbox" ${p.reducedMotion ? "checked" : ""}>${this.tr("motion")}</label><label class="check"><input id="contrast" type="checkbox" ${p.contrast ? "checked" : ""}>${this.tr("contrast")}</label><label>${this.tr("speed")}<select id="speed">${[0.5, 1, 2].map((v) => `<option value="${v}" ${p.speed === v ? "selected" : ""}>${v}×</option>`).join("")}</select></label>${this.button(this.tr("fullscreen"), "fullscreen", "chart")}${this.game ? `<hr>${this.button(this.tr("saves"), "saves", "save")}${this.button(this.tr("menu"), "menu", "home")}` : ""}<p class="muted">${this.tr("help.keys")}</p></section></div>`;
  }
  private researchView() {
    const g = this.game;
    if (!g) return "";
    const fs = g.factions[g.player];
    return `<p class="balance-line">${icon("research")}<strong>${fs.rp} RP</strong> · ${this.tr("preferred.research")}</p><div class="technology-grid">${TECHS.map(
      (type) => {
        const level = fs.tech[type];
        return `<article class="technology-card">${icon({ weapons: "weapon-tech", shields: "shield-tech", propulsion: "propulsion-tech", infrastructure: "infrastructure-tech" }[type])}<h3>${this.tr(type === "shields" ? "shields.tech" : type)}</h3><div class="tech-levels">${[1, 2, 3].map((v) => `<span class="${level >= v ? "researched" : ""}">${roman(v)}</span>`).join("")}</div><p>${this.tr(type + ".desc")}</p>${PREFERENCES[g.player].includes(type) ? `<span class="badge">★ ${this.tr("preferred.research")}</span>` : ""}${this.order({ type: "research", technology: type }, level === 3 ? this.tr("max") : this.tr("research.buy"), "research", level === 3 ? "" : `${technologyCost(g.player, type, level + 1)} RP`)}</article>`;
      },
    ).join("")}</div>`;
  }
  private fleetsView() {
    const g = this.game;
    if (!g) return "";
    const fs = g.fleets.filter((f) => f.faction === g.player),
      site =
        g.systems[this.selectedSystem]?.owner === g.player &&
        g.systems[this.selectedSystem].bastion
          ? this.selectedSystem
          : (Object.values(g.systems).find(
              (s) => s.owner === g.player && s.bastion,
            )?.id ?? HOME[g.player]);
    return `<p>${this.tr("commission.hint")}</p><div class="fleet-roster">${fs
      .map((fl) => {
        const a = admiralBonuses(fl.admiral, g.player);
        return `<article class="fleet-roster-card"><div class="admiral-profile">${img(`factions/${g.player}/${fl.admiral.role}.webp`, COMMANDERS[g.player][fl.admiral.role])}<div><h3>${COMMANDERS[g.player][fl.admiral.role]}</h3><p>${this.tr(fl.admiral.role)} · ${this.tr("rank")} ${roman(rank(fl.admiral.xp))}</p><small>${fl.admiral.xp} XP · ${this.tr("damage")} +${Math.round(a.attack * 100)}% · ${this.tr("defensive")} +${Math.round(a.resistance * 100)}%</small><p>${escape(this.sn(fl.system))} · ${this.tr("moves")} ${fl.moves}</p>${this.button(this.tr("select.fleet"), "fleet-locate", "fleet", `data-id="${fl.id}"`)}</div></div><div class="roster-ships">${fl.ships
          .map((u) => {
            const m = shipMax(
              u.type,
              g.player,
              g.factions[g.player].tech.shields,
            );
            return `<div>${img(`ships/${g.player}/${u.type}.webp`, B.factions[g.player].ships[u.type])}<b>${B.factions[g.player].ships[u.type]}</b><small>${this.tr("hull")} ${u.hull}/${m.hull} · ${this.tr("shields")} ${u.shields}/${m.shields}</small></div>`;
          })
          .join(
            "",
          )}</div><div class="ship-orders">${this.shipOffers(fl)}</div></article>`;
      })
      .join(
        "",
      )}</div><h3>${this.tr("commission")} · ${escape(this.sn(site))}</h3><div class="commission-grid">${ROLES.map((role) => `<article>${img(`factions/${g.player}/${role}.webp`, COMMANDERS[g.player][role])}<b>${COMMANDERS[g.player][role]}</b><small>${this.tr(role)}</small>${this.order({ type: "commission", system: site, role }, this.tr("commission"), "admiral", Number.isFinite(commissionCost(g, g.player)) ? `${commissionCost(g, g.player)} C` : "")}</article>`).join("")}</div><p class="muted">${this.tr("reserves")}</p>`;
  }
  private specialAction(ability: Ability): GameAction {
    const g = this.game!,
      fl =
        g.fleets.find(
          (f) => f.id === this.selectedFleet && f.faction === g.player,
        ) ?? g.fleets.find((f) => f.faction === g.player);
    return {
      type: "special",
      ability,
      system: this.selectedSystem,
      fleet: fl?.id,
      to: this.selectedSystem,
    };
  }
  private anomalyView() {
    const g = this.game;
    if (!g) return "";
    const o = observe(g, g.player),
      fl = g.fleets.filter((f) => f.faction === g.player);
    return `<p>${this.tr("special.hint")}</p><div class="special-targets"><label>${this.tr("target")}<select id="special-system">${o.systems.map((s) => `<option value="${s.id}" ${this.selectedSystem === s.id ? "selected" : ""}>${escape(this.sn(s.id))}${s.owner === g.player ? " ◆" : ""}</option>`).join("")}</select></label><label>${this.tr("select.fleet")}<select id="special-fleet">${fl.map((f) => `<option value="${f.id}" ${this.selectedFleet === f.id ? "selected" : ""}>${COMMANDERS[g.player][f.admiral.role]} · ${escape(this.sn(f.system))}</option>`).join("")}</select></label></div><p class="balance-line">${icon("anomaly")}<b>${g.factions[g.player].charge} / 200 AC</b> · ${this.tr(g.factions[g.player].specialUsed ? "used" : "ready")}</p><div class="anomaly-grid">${(Object.keys(B.specialAbilities) as Ability[]).map((a) => `<article>${icon(abilityIcon[a])}<h3>${this.tr(a)}</h3><p>${this.tr(a + ".desc")}</p>${this.order(this.specialAction(a), this.tr("activate"), abilityIcon[a], `${B.specialAbilities[a]} AC`)}</article>`).join("")}</div>`;
  }
  private recordsView(summary = false) {
    const g = this.game;
    if (!g) return "";
    const reports = g.reports
        .map((r, i) => ({ r, i }))
        .filter(({ r }) => r.attacker === g.player || r.defender === g.player),
      entries = g.ledger
        .filter(
          (e) => e.faction === g.player && (!summary || e.turn >= g.turn - 1),
        )
        .slice(-200)
        .reverse();
    return `<div class="records-layout"><section><h3>${this.tr("reports")}</h3>${
      reports.length
        ? reports
            .reverse()
            .map(
              ({ r, i }) =>
                `<button type="button" class="report-row" data-action="report" data-index="${i}">${icon(r.captured ? "victory" : "defense")}<span><b>${escape(this.sn(r.system))}</b><small>${this.tr("turn")} ${r.turn} · ${this.tr("round")} ${r.rounds} · ${this.tr("losses")} ${r.lostAttack}/${r.lostDefense}</small></span></button>`,
            )
            .join("")
        : `<p>${this.tr("empty.report")}</p>`
    }</section><section><h3>${this.tr("ledger")}</h3><div class="ledger">${entries
      .map((e) => {
        const k = "ledger." + e.key,
          label = TEXT[k] ? this.tr(k) : this.tr(e.key);
        return `<div><span class="ledger-turn">${e.turn}</span><span>${escape(label)}<small>${escape(this.nodes[e.ref] ? this.sn(e.ref) : FACTIONS.includes(e.ref as FactionId) ? this.fn(e.ref as FactionId) : TEXT[e.ref] ? this.tr(e.ref) : "")}</small></span><b class="${e.amount < 0 ? "negative" : "positive"}">${e.amount > 0 ? "+" : ""}${e.amount ? e.amount : ""} ${e.currency === "credits" ? "C" : e.currency === "rp" ? "RP" : e.currency === "charge" ? "AC" : ""}</b></div>`;
      })
      .join("")}</div></section></div>`;
  }
  private reportView(r?: BattleReport) {
    if (!r) return `<p>${this.tr("empty.report")}</p>`;
    return `<div class="report-hero"><p class="eyebrow">${this.tr("turn")} ${r.turn} · ${r.rounds} ${this.tr("round")}</p><h2>${escape(this.sn(r.system))}</h2><p>${this.fn(r.attacker)} / ${r.defender ? this.fn(r.defender) : this.tr("neutral.owner")}</p><h3>${this.tr(this.battleOutcome(r.outcome, r.attacker))}</h3></div><div class="report-statistics"><span>${this.tr("attacker")}<b>−${r.lostAttack} ${this.tr("losses")}</b><small>+${r.attackerXp} XP · ${roman(r.attackerRanks[0])} → ${roman(r.attackerRanks[1])}</small></span><span>${this.tr("defender")}<b>−${r.lostDefense} ${this.tr("losses")}</b><small>+${r.defenderXp} XP · ${roman(r.defenderRanks[0])} → ${roman(r.defenderRanks[1])}</small></span><span>${this.tr("loss.cost")}<b>${r.creditsLost} C</b></span></div>${r.captured ? `<p class="badge">${this.tr("captured")} · ${escape(this.sn(r.system))}</p>` : ""}${r.surrendered.map((f) => `<p class="badge">${this.tr("surrender")} · ${this.fn(f)}</p>`).join("")}${r.engagement ? `<h3>${this.tr("survivors")}</h3><div class="survivor-grid">${[...r.engagement.attacker.units, ...r.engagement.defender.units].map((u) => `<div class="${u.hull ? "" : "destroyed"}">${img(u.type === "bastion" ? buildingAsset(u.faction, "bastion") : `ships/${u.faction}/${u.type}.webp`, this.unitName(u))}<b>${escape(this.unitName(u))}</b><small>${this.tr("hull")} ${u.hull}/${u.maxHull} · ${this.tr("shields")} ${u.shields}/${u.maxShields}</small></div>`).join("")}</div>` : ""}${this.button(this.tr("continue"), "report-close", "play", "", "primary")}`;
  }
  private savesView() {
    return `<p>${this.tr("save.hint")}</p>${this.store.mode !== "indexeddb" ? `<p class="warning">${this.tr(this.store.mode === "memory" ? "storage.memory" : "storage.local")}</p>` : ""}<label>${this.tr("name")}<input id="save-name" maxlength="60" value="${escape(this.game ? this.fn(this.game.player) + " · " + this.tr("turn") + " " + this.game.turn : "")}"></label>${[
      false,
      true,
    ]
      .map(
        (auto) =>
          `<h3>${this.tr(auto ? "automatic" : "manual")}</h3><div class="save-slots">${SLOTS.filter(
            (s) => s.startsWith(auto ? "auto-" : "manual-"),
          )
            .map((slot) => {
              const r = this.records.find((r) => r.slot === slot);
              return `<article class="save-slot"><div><b>${this.tr("slot")} ${slot.split("-")[1]} · ${r ? escape(r.name || this.fn(r.game.player)) : this.tr("empty")}</b>${r ? `<small>${new Intl.DateTimeFormat(this.preferences.language, { dateStyle: "short", timeStyle: "short" }).format(new Date(r.date))} · ${this.tr("turn")} ${r.game.turn} · ${this.fn(r.game.player)}</small>` : ""}</div><div class="button-row">${!auto && this.game ? this.button(this.tr("save"), "save-slot", "save", `data-slot="${slot}"`) : ""}${r ? this.button(this.tr("load"), "load-slot", "load", `data-slot="${slot}"`) + this.button(this.tr("delete"), "delete-slot", "close", `data-slot="${slot}"`) : ""}</div></article>`;
            })
            .join("")}</div>`,
      )
      .join(
        "",
      )}<div class="button-row">${this.game ? this.button(this.tr("export"), "export", "export") : ""}${this.button(this.tr("import"), "import", "import")}</div><input type="file" id="save-import" accept=".json,application/json" hidden>`;
  }
  private helpView() {
    return `<div class="manual-grid">${["goal", "economy", "fog", "core", "battle", "keys"].map((k) => `<article><h3>${this.tr({ goal: "capital.goal", economy: "build", fog: "fog", core: "region", battle: "sim", keys: "doctrine" }[k as "goal"])}</h3><p>${this.tr("help." + k)}</p></article>`).join("")}</div><p>${this.tr("doctrine.hint")}</p><p>${this.tr("reserves")}</p><p>${this.tr("supply.hint")}</p><p class="muted">${this.tr("help.original")}</p>`;
  }
  private confirmView() {
    return this.confirmText
      ? `<div class="confirm-overlay"><section role="alertdialog" aria-modal="true" aria-labelledby="confirm-title" class="confirm-box"><h2 id="confirm-title">${this.tr("confirm")}</h2><p>${escape(this.confirmText)}</p><div class="button-row">${this.button(this.tr("cancel"), "confirm-no", "close")}${this.button(this.tr("confirm"), "confirm-yes", "check", "", "primary")}</div></section></div>`
      : "";
  }
  private ask(key: string, callback: () => void) {
    this.pauseAuto();
    this.confirmText = this.tr(key);
    this.confirmCallback = callback;
    this.render();
    queueMicrotask(() =>
      this.root
        .querySelector<HTMLElement>('[data-action="confirm-no"]')
        ?.focus(),
    );
  }
  private open(modal: Modal) {
    this.pauseAuto();
    const focused = document.activeElement as HTMLElement | null;
    this.focusBeforeModal = focused?.id
      ? "#" + CSS.escape(focused.id)
      : focused?.dataset.focus
        ? '[data-focus="' + focused.dataset.focus + '"]'
        : null;
    this.modal = modal;
    if (modal === "saves") void this.refreshSaves();
    this.render();
    queueMicrotask(() =>
      this.root.querySelector<HTMLElement>(".modal button")?.focus(),
    );
  }
  private close() {
    this.modal = null;
    this.render();
    if (this.focusBeforeModal)
      this.root.querySelector<HTMLElement>(this.focusBeforeModal)?.focus();
    this.focusBeforeModal = null;
    this.pumpAI();
  }
  private notify(key: string) {
    if (this.destroyed) return;
    this.toast = TEXT[key] ? this.tr(key) : key;
    window.clearTimeout(this.toastTimer);
    this.render();
    this.toastTimer = window.setTimeout(() => {
      this.toast = "";
      this.render();
    }, 4000);
  }
  private async refreshSaves() {
    try {
      this.records = await this.store.list();
      this.latest = await this.store.latest();
      this.render();
    } catch {
      this.notify("save.failed");
    }
  }
  private async autosave() {
    if (!this.game) return;
    try {
      await this.store.auto(this.game);
      await this.refreshSaves();
    } catch {
      this.notify("save.failed");
    }
  }
  private async loadSlot(slot: Slot) {
    try {
      this.adopt(await this.store.read(slot));
      this.notify("save.loaded");
    } catch {
      this.notify("save.invalid");
    }
  }
  private adopt(game: GameState) {
    this.clearFlight();
    this.clearShots();
    this.pauseAuto();
    window.clearTimeout(this.aiTimer);
    this.pumping = false;
    this.game = game;
    this.screen = "game";
    this.modal = null;
    this.selectedSystem = HOME[game.player];
    this.selectedFleet =
      game.fleets.find((f) => f.faction === game.player)?.id ?? "";
    this.lastEvents = game.battle?.events.length ?? 0;
    this.mapZoom = window.innerWidth < 600 ? 2.4 : 1;
    this.mapX =
      window.innerWidth < 600
        ? (500 - this.nodes[this.selectedSystem].x) * this.mapZoom
        : 0;
    this.mapY =
      window.innerWidth < 600
        ? (380 - this.nodes[this.selectedSystem].y) * this.mapZoom
        : 0;
    this.render();
    this.pumpAI();
  }
  private launch() {
    this.adopt(
      createGame({
        faction: this.setupFaction,
        role: this.setupRole,
        opponents: this.opponents,
        difficulty: this.difficulty,
        seed:
          this.seed.trim().replace(/[^\p{L}\p{N} .:_-]/gu, "-") ||
          `Meridian-${Date.now().toString(36)}-${crypto.getRandomValues(new Uint32Array(1))[0].toString(36)}`,
        tutorial: this.guided,
      }),
    );
    void this.autosave();
  }
  private effect(kind: string) {
    this.commandFx = kind;
    window.clearTimeout(this.fxTimer);
    this.fxTimer = window.setTimeout(() => {
      this.commandFx = "";
      this.render();
    }, 850);
  }
  private perform(a: GameAction) {
    if (!this.game || this.flight || actor(this.game) !== this.game.player)
      return;
    try {
      const previous = this.game,
        before = previous.phase,
        oldPlayer = previous.player;
      this.game = act(this.game, a);
      const sfx =
        a.type === "build"
          ? a.building === "mine"
            ? "mine"
            : "station"
          : a.type === "ship"
            ? "ship"
            : a.type === "research"
              ? "research"
              : a.type === "move"
                ? "jump"
                : a.type === "commission"
                  ? "fleet"
                  : a.type === "special"
                    ? a.ability === "repairPulse"
                      ? "repair"
                      : a.ability === "sensorBurst"
                        ? "scan"
                        : a.ability === "slipstreamCorridor"
                          ? "jump"
                          : "special"
                    : "click";
      void this.audio.sfx(sfx);
      if (
        a.type !== "move" &&
        !(a.type === "special" && a.ability === "slipstreamCorridor")
      )
        this.effect(
          a.type === "research"
            ? "levelup"
            : a.type === "special"
              ? abilityIcon[a.ability] === "sabotage"
                ? "missile"
                : abilityIcon[a.ability] === "repair"
                  ? "repair"
                  : abilityIcon[a.ability] === "jump"
                    ? "jump"
                    : abilityIcon[a.ability] === "phase"
                      ? "phase"
                      : abilityIcon[a.ability] === "ion"
                        ? "ion"
                        : "scan"
              : "capture",
        );
      if (
        a.type === "move" &&
        this.game.phase === "battle" &&
        before !== "battle"
      ) {
        this.lastEvents = 0;
        if (this.game.tutorial >= 0) this.game.tutorial = 3;
      }
      if (!this.game.fleets.some((f) => f.id === this.selectedFleet))
        this.selectedFleet =
          this.game.fleets.find((f) => f.faction === oldPlayer)?.id ?? "";
      this.startFlight(previous, a);
      if (
        a.type === "move" ||
        (a.type === "special" && a.ability === "slipstreamCorridor" && a.to)
      )
        this.selectedSystem = a.to!;
      this.render();
      if (a.type === "end") this.pumpAI();
      if (this.game.phase === "victory" || this.game.phase === "defeat") {
        void this.audio.sfx(this.game.phase);
        void this.autosave();
      }
    } catch (e) {
      void this.audio.sfx("error");
      this.notify(e instanceof RuleError ? e.key : "save.invalid");
    }
  }
  private stepBattle() {
    if (!this.game?.battle || this.game.battle.status !== "active") return;
    this.clearShots();
    this.lastEvents = this.game.battle.events.length;
    this.previousHull = new Map(
      [
        ...this.game.battle.attacker.units,
        ...this.game.battle.defender.units,
      ].map((u) => [u.id, u.hull]),
    );
    this.game = battleRound(
      this.game,
      this.game.battle[playerBattleSide(this.game)].stance,
    );
    this.shots = shotsFor(
      this.game.battle!,
      this.game.battle!.events.slice(this.lastEvents),
      playerBattleSide(this.game),
      this.preferences.speed,
    );
    for (const shot of this.shots) {
      const sound =
        shot.source.type === "dreadnought"
          ? "plasma"
          : ["beam", "beam-2", "beam-3"][this.beam++ % 3];
      this.shotTimers.push(
        window.setTimeout(() => void this.audio.sfx(sound), shot.delay),
      );
      if (shot.event.type === "hit")
        this.shotTimers.push(
          window.setTimeout(() => {
            void this.audio.sfx(
              shot.destroys
                ? "explosion"
                : shot.event.shields
                  ? "shield"
                  : "hull",
            );
          }, shot.impact),
        );
    }
    this.roundDuration =
      (this.shots.at(-1)?.impact ?? 0) +
      Math.round(600 / this.preferences.speed);
    this.shotTimers.push(
      window.setTimeout(() => {
        this.clearShots();
        this.render();
      }, this.roundDuration),
    );
    if (this.game.battle!.status !== "active") this.pauseAuto();
    this.render();
  }
  private pauseAuto() {
    this.auto = false;
    window.clearTimeout(this.autoTimer);
  }
  private toggleAuto() {
    if (this.auto) {
      this.pauseAuto();
      this.render();
      return;
    }
    if (!this.game?.battle || this.game.battle.status !== "active") return;
    this.auto = true;
    const tick = () => {
      if (!this.auto || this.modal || this.confirmText) return;
      this.stepBattle();
      if (this.auto)
        this.autoTimer = window.setTimeout(
          tick,
          Math.max(this.roundDuration + 40, 750 / this.preferences.speed),
        );
    };
    this.render();
    this.autoTimer = window.setTimeout(tick, 100);
  }
  private resolve() {
    if (!this.game?.battle || this.game.battle.status === "active") return;
    this.pauseAuto();
    this.clearShots();
    const b = this.game.battle,
      won =
        (b.status === "attacker" && b.attacker.faction === this.game.player) ||
        (b.status !== "attacker" && b.defender.faction === this.game.player);
    this.game = concludeBattle(this.game);
    this.reportIndex = this.game.reports.length - 1;
    const r = this.game.reports[this.reportIndex];
    if (
      r.attackerRanks[1] > r.attackerRanks[0] ||
      r.defenderRanks[1] > r.defenderRanks[0]
    )
      void this.audio.sfx("levelup");
    else void this.audio.sfx(won ? "capture" : "defeat");
    this.open("report");
    void this.autosave();
  }
  private pumpAI() {
    if (
      this.pumping ||
      this.modal ||
      !this.game ||
      this.screen !== "game" ||
      this.game.phase === "victory" ||
      this.game.phase === "defeat"
    )
      return;
    if (
      this.game.phase === "battle" &&
      (this.game.battle?.attacker.faction === this.game.player ||
        this.game.battle?.defender.faction === this.game.player)
    )
      return;
    if (this.game.phase === "map" && actor(this.game) === this.game.player)
      return;
    this.pumping = true;
    const step = () => {
      if (this.destroyed || this.modal || !this.game) {
        this.pumping = false;
        return;
      }
      let g = this.game;
      try {
        if (g.phase === "battle") {
          if (
            g.battle!.attacker.faction === g.player ||
            g.battle!.defender.faction === g.player
          ) {
            this.pumping = false;
            this.lastEvents = 0;
            this.render();
            return;
          }
          while (g.battle!.status === "active")
            g = battleRound(
              g,
              aiStance(g.battle!.attacker, g.battle!.defender, g.difficulty),
              "attacker",
            );
          g = concludeBattle(g);
          this.game = g;
          void this.autosave();
        } else if (g.phase === "map" && actor(g) !== g.player) {
          const a =
            g.actorActions >= 40
              ? ({ type: "end" } as GameAction)
              : decide(observe(g, actor(g))).action;
          let applied = true;
          try {
            this.game = act(g, a);
          } catch (e) {
            if (!(e instanceof RuleError)) throw e;
            applied = false;
            this.game = act(g, { type: "end" });
          }
          const travel = applied ? this.startFlight(g, a) : 0;
          if (travel) {
            void this.audio.sfx("jump");
            this.render();
            this.aiTimer = window.setTimeout(step, travel + 20);
            return;
          }
          if (a.type === "end") this.render();
        } else {
          this.pumping = false;
          this.render();
          if (g.phase === "map") {
            void this.autosave();
            this.open("summary");
          } else {
            void this.audio.sfx(g.phase);
            void this.autosave();
          }
          return;
        }
      } catch (e) {
        this.pumping = false;
        this.notify(e instanceof Error ? e.message : "save.invalid");
        return;
      }
      this.aiTimer = window.setTimeout(step, 0);
    };
    this.aiTimer = window.setTimeout(step, 20);
  }
  private click = (e: Event) => {
    const target = (e.target as Element).closest<HTMLElement>("[data-action]");
    if (!target || target.hasAttribute("disabled")) return;
    const a = target.dataset.action!;
    if (
      this.flight &&
      [
        "order",
        "end",
        "system",
        "fleet-select",
        "fleet-locate",
        "stance",
        "next",
        "auto",
        "skip",
        "retreat",
        "resolve",
      ].includes(a)
    )
      return;
    if (a === "menu-home" || a === "system") e.preventDefault();
    if (
      !["system", "fleet-select", "preview", "zoom-in", "zoom-out"].includes(a)
    )
      void this.audio.sfx("click");
    if (a === "language") {
      this.preferences.language = target.dataset.language as Language;
      savePreferences(this.preferences);
      this.render();
      return;
    }
    if (a === "confirm-no") {
      this.confirmText = "";
      this.confirmCallback = null;
      this.render();
      return;
    }
    if (a === "confirm-yes") {
      const fn = this.confirmCallback;
      this.confirmText = "";
      this.confirmCallback = null;
      this.render();
      fn?.();
      return;
    }
    if (a === "close") {
      this.close();
      return;
    }
    if (a === "sound") {
      if (this.preferences.muted || this.audio.needsGesture) {
        this.preferences.muted = false;
        void this.audio.unlock();
      } else this.preferences.muted = true;
      this.audio.apply();
      savePreferences(this.preferences);
      this.render();
      return;
    }
    if (a === "new") {
      const go = () => {
        this.pauseAuto();
        this.clearFlight();
        this.clearShots();
        window.clearTimeout(this.aiTimer);
        this.pumping = false;
        this.screen = "setup";
        this.modal = null;
        this.render();
      };
      if (this.game && this.game.phase === "map" && this.screen === "game")
        this.ask("menu.confirm", go);
      else go();
      return;
    }
    if (a === "preview" || a === "faction") {
      this.setupFaction = target.dataset.faction as FactionId;
      if (a === "faction") this.setupRole = PREFERRED_ROLE[this.setupFaction];
      this.render();
      return;
    }
    if (a === "role") {
      this.setupRole = target.dataset.role as Role;
      this.render();
      return;
    }
    if (a === "start") {
      this.launch();
      return;
    }
    if (a === "menu-home") {
      this.clearFlight();
      this.clearShots();
      this.screen = "menu";
      this.modal = null;
      this.render();
      return;
    }
    if (a === "menu") {
      this.ask("menu.confirm", () => {
        this.pauseAuto();
        this.clearFlight();
        this.clearShots();
        window.clearTimeout(this.aiTimer);
        this.pumping = false;
        this.screen = "menu";
        this.modal = null;
        this.render();
      });
      return;
    }
    if (a === "continue" && this.latest) {
      void this.loadSlot(this.latest.slot);
      return;
    }
    if (
      [
        "settings",
        "research",
        "fleets",
        "anomalies",
        "records",
        "saves",
        "help",
        "credits",
        "combat-log",
      ].includes(a)
    ) {
      this.open(a as Modal);
      return;
    }
    if (a === "fullscreen") {
      const request = document.fullscreenElement
        ? document.exitFullscreen()
        : document.documentElement.requestFullscreen?.();
      void request?.catch(() => this.notify("fullscreen.fail"));
      if (!request) this.notify("fullscreen.fail");
      return;
    }
    if (a === "order") {
      this.perform(JSON.parse(target.dataset.order!));
      return;
    }
    if (a === "system") {
      this.selectedSystem = target.dataset.system!;
      void this.audio.sfx("system");
      this.render();
      return;
    }
    if (a === "fleet-select" || a === "fleet-locate") {
      const fl = this.game?.fleets.find((f) => f.id === target.dataset.id);
      if (fl) {
        this.selectedFleet = fl.id;
        this.selectedSystem = fl.system;
        if (a === "fleet-locate") {
          this.modal = null;
          this.mapZoom = window.innerWidth < 600 ? 2.4 : 1.4;
          this.mapX = (500 - this.nodes[fl.system].x) * this.mapZoom;
          this.mapY = (380 - this.nodes[fl.system].y) * this.mapZoom;
        }
        void this.audio.sfx("fleet");
        this.render();
      }
      return;
    }
    if (a === "zoom-in" || a === "zoom-out") {
      this.mapZoom = Math.min(
        3,
        Math.max(0.8, this.mapZoom * (a === "zoom-in" ? 1.2 : 1 / 1.2)),
      );
      this.updateMap();
      return;
    }
    if (a === "center") {
      this.mapZoom = 1;
      this.mapX = this.mapY = 0;
      this.updateMap();
      return;
    }
    if (a === "end") {
      this.ask("end.confirm", () => this.perform({ type: "end" }));
      return;
    }
    if (a === "tutorial-next" && this.game) {
      this.game.tutorial =
        this.game.tutorial >= 4 ? -1 : this.game.tutorial + 1;
      this.render();
      return;
    }
    if (a === "tutorial-dismiss" && this.game) {
      this.game.tutorial = -1;
      this.render();
      return;
    }
    if (a === "stance" && this.game) {
      this.game = chooseStance(this.game, target.dataset.stance as Stance);
      this.render();
      return;
    }
    if (a === "next") {
      this.stepBattle();
      return;
    }
    if (a === "auto") {
      this.toggleAuto();
      return;
    }
    if (a === "skip" && this.game?.battle) {
      this.pauseAuto();
      this.clearShots();
      this.lastEvents = this.game.battle.events.length;
      while (this.game.battle?.status === "active")
        this.game = battleRound(
          this.game,
          this.game.battle[playerBattleSide(this.game)].stance,
        );
      this.render();
      return;
    }
    if (
      a === "retreat" &&
      this.game &&
      canRetreat(this.game) &&
      this.game.battle!.round >= 1
    ) {
      this.ask("retreat.confirm", () => {
        if (this.game) {
          this.clearShots();
          this.game = retreat(this.game);
          void this.audio.sfx("retreat");
          this.render();
        }
      });
      return;
    }
    if (a === "resolve") {
      this.resolve();
      return;
    }
    if (a === "report") {
      this.reportIndex = Number(target.dataset.index);
      this.open("report");
      return;
    }
    if (a === "report-close") {
      this.close();
      return;
    }
    if (a === "export" && this.game) {
      try {
        exportSave(this.game);
      } catch {
        this.notify("save.invalid");
      }
      return;
    }
    if (a === "import") {
      this.root.querySelector<HTMLInputElement>("#save-import")?.click();
      return;
    }
    if (a === "save-slot" && this.game) {
      const slot = target.dataset.slot as Slot,
        name =
          this.root.querySelector<HTMLInputElement>("#save-name")?.value ?? "";
      const fn = () => {
        if (this.game)
          void this.store
            .write(slot, this.game, name)
            .then(() => this.refreshSaves())
            .then(() => this.notify("save.ok"))
            .catch(() => this.notify("save.failed"));
      };
      if (this.records.some((r) => r.slot === slot))
        this.ask("save.overwrite", fn);
      else fn();
      return;
    }
    if (a === "load-slot") {
      const slot = target.dataset.slot as Slot;
      if (this.game)
        this.ask("save.load.confirm", () => void this.loadSlot(slot));
      else void this.loadSlot(slot);
      return;
    }
    if (a === "delete-slot") {
      this.ask(
        "save.delete.confirm",
        () =>
          void this.store
            .remove(target.dataset.slot as Slot)
            .then(() => this.refreshSaves())
            .catch(() => this.notify("save.failed")),
      );
      return;
    }
  };
  private change = (e: Event) => {
    const el = e.target as HTMLInputElement;
    if (el.id === "opponents") this.opponents = Number(el.value);
    else if (el.id === "difficulty")
      this.difficulty = el.value as GameState["difficulty"];
    else if (el.id === "guided") this.guided = el.checked;
    else if (el.id === "special-system") {
      this.selectedSystem = el.value;
      this.render();
    } else if (el.id === "special-fleet") {
      this.selectedFleet = el.value;
      this.render();
    } else if (["muted", "reducedMotion", "contrast"].includes(el.id)) {
      this.preferences[el.id as "muted" | "reducedMotion" | "contrast"] =
        el.checked;
      savePreferences(this.preferences);
      this.audio.apply();
      if (el.id === "muted" && !el.checked && this.audio.needsGesture)
        void this.audio.unlock();
      this.render();
    } else if (el.id === "speed") {
      this.preferences.speed = Number(el.value);
      savePreferences(this.preferences);
    } else if (el.id === "save-import" && el.files?.[0]) {
      void importFile(el.files[0])
        .then((g) => {
          this.pendingImport = g;
          this.ask("save.import.confirm", () => {
            if (this.pendingImport) {
              this.adopt(this.pendingImport);
              this.pendingImport = null;
              void this.autosave();
            }
          });
        })
        .catch((err) =>
          this.notify(
            err instanceof Error && err.message === "save.size"
              ? "save.size"
              : "save.invalid",
          ),
        );
    }
  };
  private input = (e: Event) => {
    const el = e.target as HTMLInputElement;
    if (el.id === "seed") this.seed = el.value;
    if (["master", "music", "sfx"].includes(el.id)) {
      this.preferences[el.id as "master" | "music" | "sfx"] =
        Number(el.value) / 100;
      savePreferences(this.preferences);
      this.audio.apply();
      const label = el.closest("label")?.querySelector("b");
      if (label) label.textContent = el.value + "%";
    }
  };
  private key = (e: KeyboardEvent) => {
    const target = e.target as HTMLElement;
    if (e.altKey || e.ctrlKey || e.metaKey) return;
    if (e.key === "Escape") {
      e.preventDefault();
      if (this.confirmText) {
        this.confirmText = "";
        this.confirmCallback = null;
        this.render();
      } else if (this.modal) this.close();
      else if (this.auto) {
        this.pauseAuto();
        this.render();
      } else if (this.game && this.screen === "game") this.open("settings");
      return;
    }
    const dialog =
      this.root.querySelector<HTMLElement>(".confirm-box") ??
      this.root.querySelector<HTMLElement>(".modal");
    if (dialog && e.key === "Tab") {
      const els = [
          ...dialog.querySelectorAll<HTMLElement>(
            "button:not(:disabled),input:not([hidden]),select,textarea,a[href]",
          ),
        ],
        first = els[0],
        last = els[els.length - 1];
      if (e.shiftKey && (!dialog.contains(target) || target === first)) {
        e.preventDefault();
        last?.focus();
      } else if (!e.shiftKey && (!dialog.contains(target) || target === last)) {
        e.preventDefault();
        first?.focus();
      }
      return;
    }
    if (
      target.matches("input,select,textarea") ||
      target.isContentEditable ||
      this.modal ||
      this.confirmText
    )
      return;
    if (
      target.closest("[data-system]") &&
      (e.key === "Enter" || e.key === " ")
    ) {
      e.preventDefault();
      this.selectedSystem =
        target.closest<HTMLElement>("[data-system]")!.dataset.system!;
      void this.audio.sfx("system");
      this.render();
      return;
    }
    if (this.screen !== "game" || !this.game || this.flight) return;
    const contact = target.closest<HTMLElement>(".map-fleet.own");
    if (contact && (e.key === "Enter" || e.key === " ")) {
      const fl = this.game.fleets.find(
        (f) =>
          f.id === contact.dataset.fleet && f.faction === this.game!.player,
      );
      if (fl) {
        this.selectedFleet = fl.id;
        this.selectedSystem = fl.system;
        void this.audio.sfx("fleet");
        this.render();
      }
      e.preventDefault();
      return;
    }
    const k = e.key.toLowerCase();
    if (this.game.phase === "battle") {
      if (
        ["1", "2", "3"].includes(k) &&
        this.game.battle?.status === "active"
      ) {
        this.game = chooseStance(
          this.game,
          (["offensive", "neutral", "defensive"] as Stance[])[Number(k) - 1],
        );
        this.render();
        e.preventDefault();
      } else if (k === "a") {
        this.toggleAuto();
        e.preventDefault();
      } else if (k === "r") {
        this.root
          .querySelector<HTMLElement>('[data-action="retreat"]')
          ?.click();
        e.preventDefault();
      } else if (k === " " && !target.closest("button")) {
        this.stepBattle();
        e.preventDefault();
      }
    } else if (
      this.game.phase === "map" &&
      actor(this.game) === this.game.player
    ) {
      if (k === "e") {
        this.ask("end.confirm", () => this.perform({ type: "end" }));
        e.preventDefault();
      }
      if (k === "h") {
        this.selectedSystem = HOME[this.game.player];
        this.mapZoom = window.innerWidth < 600 ? 2.4 : 1.4;
        this.mapX = (500 - this.nodes[this.selectedSystem].x) * this.mapZoom;
        this.mapY = (380 - this.nodes[this.selectedSystem].y) * this.mapZoom;
        this.render();
      }
      const pan: Record<string, [number, number]> = {
        arrowleft: [60, 0],
        a: [60, 0],
        arrowright: [-60, 0],
        d: [-60, 0],
        arrowup: [0, 60],
        w: [0, 60],
        arrowdown: [0, -60],
        s: [0, -60],
      };
      if (pan[k]) {
        this.mapX = Math.max(-1600, Math.min(1600, this.mapX + pan[k][0]));
        this.mapY = Math.max(-1200, Math.min(1200, this.mapY + pan[k][1]));
        this.updateMap();
        e.preventDefault();
      }
      if (k === "+" || k === "-") {
        this.mapZoom = Math.min(
          3,
          Math.max(0.8, this.mapZoom * (k === "+" ? 1.2 : 1 / 1.2)),
        );
        this.updateMap();
        e.preventDefault();
      }
    }
  };
  private updateMap() {
    this.root
      .querySelector("#map-layer")
      ?.setAttribute(
        "transform",
        `translate(${500 + this.mapX} ${380 + this.mapY}) scale(${this.mapZoom}) translate(-500 -380)`,
      );
    const svg = this.root.querySelector<SVGSVGElement>(".chart");
    if (!svg) return;
    const box = svg.getBoundingClientRect();
    const scale = Math.max(
      0.05,
      Math.min((box.width || 1070) / 1070, (box.height || 800) / 800) *
        this.mapZoom,
    );
    for (const text of svg.querySelectorAll<SVGTextElement>(
      ".map-node text,.map-fleet text",
    )) {
      text.style.fontSize = `${(text.classList.contains("star-label") ? 8 : text.classList.contains("contact-count") ? 10 : 11) / scale}px`;
    }
    const nodes = nodesOf(this.displayGame);
    const charted = [
      ...svg.querySelectorAll<SVGGElement>(".map-node[data-system]"),
    ].map((node) => nodes[node.dataset.system!]);
    for (const disk of svg.querySelectorAll<SVGCircleElement>(".hit-area")) {
      const id = disk.closest<SVGGElement>("[data-system]")!.dataset.system!;
      const n = nodes[id];
      const nearest = Math.min(
        ...charted
          .filter((other) => other.id !== id)
          .map((other) => Math.hypot(n.x - other.x, n.y - other.y)),
      );
      disk.setAttribute("r", String(planetHitRadius(scale, nearest)));
    }
  }
  private updateBattleLayout() {
    const arena = this.root.querySelector<HTMLElement>(".space-arena");
    const plane = arena?.querySelector<HTMLElement>(".combat-units");
    const b = this.game?.battle;
    if (!arena || !plane || !b) return;
    const height = plane.getBoundingClientRect().height;
    if (height > 0)
      arena.style.setProperty(
        "--sprite-height",
        `${battleSpriteHeight(height, Math.max(b.attacker.units.length, b.defender.units.length))}px`,
      );
  }
  private pointer = (e: PointerEvent) => {
    const chart = (e.target as Element).closest<HTMLElement>("#chart");
    if (!chart || (e.target as Element).closest("[data-system],[data-fleet]"))
      return;
    let lastX = e.clientX,
      lastY = e.clientY;
    chart.setPointerCapture?.(e.pointerId);
    const move = (v: PointerEvent) => {
      const box = chart.getBoundingClientRect();
      const scale =
        1 / Math.max(0.05, Math.min(box.width / 1070, box.height / 800));
      this.mapX = Math.max(
        -1600,
        Math.min(1600, this.mapX + (v.clientX - lastX) * scale),
      );
      this.mapY = Math.max(
        -1200,
        Math.min(1200, this.mapY + (v.clientY - lastY) * scale),
      );
      lastX = v.clientX;
      lastY = v.clientY;
      this.updateMap();
    };
    const up = () => {
      chart.removeEventListener("pointermove", move);
      chart.removeEventListener("pointerup", up);
      chart.removeEventListener("pointercancel", up);
    };
    chart.addEventListener("pointermove", move);
    chart.addEventListener("pointerup", up);
    chart.addEventListener("pointercancel", up);
  };
  private wheel = (e: WheelEvent) => {
    if (!(e.target as Element).closest("#chart")) return;
    e.preventDefault();
    this.mapZoom = Math.max(
      0.8,
      Math.min(3, this.mapZoom * (e.deltaY < 0 ? 1.1 : 1 / 1.1)),
    );
    this.updateMap();
  };
  destroy() {
    this.destroyed = true;
    this.layoutObserver?.disconnect();
    window.removeEventListener("resize", this.resize);
    this.pauseAuto();
    window.clearTimeout(this.aiTimer);
    window.clearTimeout(this.toastTimer);
    window.clearTimeout(this.fxTimer);
    this.clearFlight();
    this.clearShots();
    this.audio.dispose();
    this.root.removeEventListener("click", this.click);
    this.root.removeEventListener("change", this.change);
    this.root.removeEventListener("input", this.input);
    document.removeEventListener("keydown", this.key);
    document.removeEventListener("pointerdown", this.automaticSound, true);
    document.removeEventListener("keydown", this.automaticSound, true);
    this.root.removeEventListener("pointerdown", this.pointer);
    this.root.removeEventListener("wheel", this.wheel);
  }
}
